/**
 * Per-property financing metadata, backed by the `loans` table.
 *
 * The active financing for a pipeline property — lender, the master
 * enabled/all-cash toggle, and which loan slot (`loan_type`: hard_money =
 * short, refinance/conventional = long) — lives in `loans`, NOT in the
 * property_setting strategy blobs. The actual loan terms (ltv/rate/term) still
 * live in the property_setting scenario columns for now, so these rows carry
 * only metadata (terms NULL) with `status = 'pending'` (kept out of accounting,
 * which sums interest over `status = 'active'` loans).
 *
 * Mutual exclusivity (short XOR long) is one enabled row per property — a DB
 * partial unique index backs the single-row model used here.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import type { Loan, LoanType } from "@/hooks/use-loans";
import { toUserMessage } from "@/lib/supabase-errors";

export interface FinancingInput {
  /** Active loan slot: hard_money (short) | refinance | conventional (long). */
  loan_type: LoanType;
  lender: string | null;
  /** false = held all-cash. */
  enabled: boolean;
}

export function useDealLoans(pipelinePropertyId?: string | null) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const key = useMemo(
    () => ["deal_loans", pipelinePropertyId] as const,
    [pipelinePropertyId],
  );

  const query = useQuery({
    queryKey: key,
    enabled: Boolean(pipelinePropertyId && currentWorkspace?.id),
    queryFn: async (): Promise<Loan[]> => {
      if (!pipelinePropertyId) return [];
      const { data, error } = await supabase
        .from("loans")
        .select("*")
        .eq("pipeline_property_id", pipelinePropertyId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Loan[];
    },
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);
  // One financing row per property in this phase: prefer the enabled one.
  const financingRow = useMemo(
    () => rows.find((r) => r.is_enabled) ?? rows[0] ?? null,
    [rows],
  );

  const saveFinancing = useMutation({
    mutationFn: async (input: FinancingInput) => {
      if (!pipelinePropertyId || !currentWorkspace?.id || !user?.id) {
        throw new Error("Missing pipeline property / workspace");
      }
      // Re-read the current row id rather than trusting the closure snapshot.
      const { data: existing, error: lookupErr } = await supabase
        .from("loans")
        .select("id")
        .eq("pipeline_property_id", pipelinePropertyId)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (lookupErr) throw lookupErr;

      const fields = {
        loan_type: input.loan_type,
        lender_name: input.lender,
        is_enabled: input.enabled,
      };

      if (existing?.id) {
        const { error } = await supabase
          .from("loans")
          .update(fields as never)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("loans").insert({
          ...fields,
          workspace_id: currentWorkspace.id,
          pipeline_property_id: pipelinePropertyId,
          created_by_user_id: user.id,
          status: "pending",
        } as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success("Financing updated");
    },
    onError: (e: unknown) =>
      toast.error(toUserMessage(e, "Failed to save financing")),
  });

  return {
    financingRow,
    rows,
    isLoading: query.isLoading,
    saveFinancing,
    refetch: query.refetch,
  };
}
